/**
 * Shared vertical spacing for overlay content so panels and indicators stay in sync.
 *
 * Top bar height is 15% of viewport (BarTop h-[15%]). The space we leave between the bars
 * and the side panels must be at least: top-bar-to-pill offset + a small gap.
 * Since the history indicator pill is only shown when the panel is closed, panels can use
 * full height without accounting for pill height.
 *
 * HistoryIndicator pill: py-1.5 (0.375rem × 2) + content (icon h-3.5 = 0.875rem) = 1.625rem height.
 * (Kept for reference, but not used in panel spacing calculations)
 */
export const BAR_TOP_HEIGHT_PERCENT = 15;
/** Space between top bar and top of pill */
export const PILL_TOP_OFFSET_REM = 0.875;
/** Viewing history pill height: py-1.5 + icon h-3.5 (px-3/gap-2 don't affect height) */
export const PILL_HEIGHT_REM = 0.375 * 2 + 0.875; // 1.625
/** Extra gap between pill and panel edge */
export const PANEL_PILL_GAP_REM = 0.875;

/** @deprecated Use PANEL_BOTTOM_INSET_REM for bottom, PANEL_TOP_INSET_REM for top. */
export const PANEL_VERTICAL_INSET_REM = PILL_TOP_OFFSET_REM + PANEL_PILL_GAP_REM;

/** Bottom bar height (match BarBottom h-[15%]). */
export const BAR_BOTTOM_HEIGHT_PERCENT = 15;

/** Inset for corner FABs: same distance from screen edges and from bars (top/bottom).
 *  Use for left/right and for gap below top bar / above bottom bar so all corners are even. */
export const FAB_EDGE_INSET_REM = 0.75;
/** Gap above bottom bar for FABs (same as edge inset for even corner margins). */
export const FAB_BOTTOM_INSET_REM = FAB_EDGE_INSET_REM;

/** Panel bottom inset: same as FAB distance from bottom bar so panel aligns with FAB spacing. */
export const PANEL_BOTTOM_INSET_REM = FAB_BOTTOM_INSET_REM;

/**
 * Height of the zoom control row when laid out horizontally (e.g. when chat panel is open).
 * One row = one FAB height (matches FAB_SIZE_REM_MD). Panels use this so they start below
 * the zoom controls without overlapping them.
 */
export const ZOOM_ROW_HEIGHT_REM = 2.5;

/** Vertical gap between zoom control row and panel top edge; same as FAB edge inset for consistency. */
export const ZOOM_PANEL_GAP_REM = FAB_EDGE_INSET_REM;

/**
 * Space from top bar bottom to panel top: leaves room for the zoom control row (top-right).
 * When chat is open zoom controls are horizontal (one row); gap below zoom matches FAB spacing.
 */
export const PANEL_TOP_INSET_REM = FAB_EDGE_INSET_REM + ZOOM_ROW_HEIGHT_REM + ZOOM_PANEL_GAP_REM;

/** Sticky app header height (matches `nav.h-16` in page chrome, e.g. results layout). */
export const APP_HEADER_HEIGHT_REM = 4;

/**
 * Thin loading bar under the header when shown (`h-1`).
 */
export const APP_HEADER_LOADING_BAR_REM = 0.25;

/**
 * `top` offset for fixed history/chat panels on the results screen (not the in-game overlay).
 * Sits below the real menu bar + loading strip + small gap — does not use {@link BAR_TOP_HEIGHT_PERCENT}.
 */
export const PANEL_TOP_RESULTS_REM =
  APP_HEADER_HEIGHT_REM + APP_HEADER_LOADING_BAR_REM + FAB_EDGE_INSET_REM;

/**
 * Bottom inset for side panels on the results screen only: small gap from the viewport edge.
 * Does not reserve the in-game bottom bar — maximizes panel height under the top nav.
 */
export const PANEL_BOTTOM_INSET_RESULTS_REM = FAB_EDGE_INSET_REM;

/**
 * Shared `top` / `bottom` for HistoryPanel and Chat when `layout="results"`.
 * Keeps both panels vertically aligned (same inset from header and viewport bottom).
 */
export const RESULTS_LAYOUT_PANEL_VERTICAL = {
  top: `${PANEL_TOP_RESULTS_REM}rem`,
  bottom: `${PANEL_BOTTOM_INSET_RESULTS_REM}rem`,
} as const;

/**
 * Shared vertical alignment for results-mode floating open buttons (mirrored left/right).
 */
export const RESULTS_LAYOUT_FAB_VERTICAL_CLASS = 'top-1/2 -translate-y-1/2';

/**
 * Top inset for the left-side history panel only. Zoom controls are on the right, so history
 * can use full height with just a small gap below the top bar (no zoom row reserved).
 */
export const PANEL_TOP_INSET_LEFT_REM = FAB_EDGE_INSET_REM;

/**
 * Top inset for the left-side panels (history, debug) when the spectator LIVE pill is visible.
 * Pill sits at BAR_TOP + FAB_EDGE_INSET_REM; we reserve pill height + gap so panels start below it.
 */
export const PANEL_TOP_INSET_LEFT_SPECTATOR_REM =
  FAB_EDGE_INSET_REM + PILL_HEIGHT_REM + PANEL_PILL_GAP_REM;
/** Gap between stacked FABs (e.g. history FAB and debug button). */
export const FAB_STACK_GAP_REM = 0.5;
/** FAB button size (match zoom control): base = w-9 (2.25rem), md = w-10 (2.5rem). */
export const FAB_SIZE_REM = 2.25;
export const FAB_SIZE_REM_MD = 2.5;

/** Bottom offset for TanStack Query devtools button (one level above history FAB). */
export const TANSTACK_DEVTOOLS_BOTTOM_OFFSET_REM =
  FAB_BOTTOM_INSET_REM + FAB_SIZE_REM + FAB_STACK_GAP_REM;

/** Bottom offset for debug panel button (two levels above history FAB, above TanStack). */
export const DEBUG_BUTTON_BOTTOM_OFFSET_REM =
  FAB_BOTTOM_INSET_REM + FAB_SIZE_REM + FAB_STACK_GAP_REM + FAB_SIZE_REM + FAB_STACK_GAP_REM;

/** Camera control cluster: compact insets (all breakpoints smaller than before).
 * Gap below top bar: pt-1.5 (0.375rem). Right edge: pr-3 md:pr-4 lg:pr-5. */
export const CAMERA_TOP_GAP_REM = 0.375;
/** Right-edge inset: mobile 0.75rem, md 1rem, lg 1.25rem. Use pr-3 md:pr-4 lg:pr-5. */
export const CAMERA_EDGE_INSET_REM = 0.75;
export const CAMERA_EDGE_INSET_REM_MD = 1;
export const CAMERA_EDGE_INSET_REM_LG = 1.25;
